import { PhoneNumberType } from '../../../interfaces/document';
import { LeadSystemSource } from '../../../interfaces/onboarding';
import {
  ReferralDemographics,
  ReferralInsurance,
  UpdateReferralRequest,
} from '../../../interfaces/referral';

/** The referral card's edit state — every field is a string, as the inputs produce them. */
export interface ReferralEditState {
  businessClinicId: number | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  mobilePhone: string;
  homePhone: string;
  otherPhone: string;
  bmi: string;
  heightFeet: string;
  heightInch: string;
  weightLb: string;
  insurance: ReferralInsurance[];
  isCommunityReferral: boolean;
  systemSource: string;
  referringProvider: string;
  communityReferralClinicName: string;
}

/**
 * The wire value for each phone input.
 *
 * `PhoneNumberType`, not string literals: the mobile input sends `cell`, and an earlier
 * revision of this file hardcoded `'mobile'` — silently changing what the PATCH body carried
 * while the extraction that introduced it was meant to preserve behaviour exactly.
 */
const PHONE_FIELDS: Array<{ key: keyof ReferralEditState; type: PhoneNumberType }> = [
  { key: 'mobilePhone', type: PhoneNumberType.Cell },
  { key: 'homePhone', type: PhoneNumberType.Home },
  { key: 'otherPhone', type: PhoneNumberType.Other },
];

/**
 * Turns the card's edit state into the PATCH body.
 *
 * Extracted from the component so it can be tested: the bug this replaced was that
 * `referringProvider` and `communityReferralClinicName` were collected by the form, shown as
 * changed, and then simply not present in the object sent — a save that succeeded and
 * discarded the edit. Nothing about rendering was involved, so nothing about rendering needs
 * to be involved in proving it stays fixed.
 *
 * Two rules govern the top-level fields:
 *
 * - `undefined` means "not edited" and is stripped before sending.
 * - `''` means "cleared" and must survive, or blanking a field silently keeps the old value.
 *
 * **Demographics do not follow the second rule**, deliberately. They use `|| undefined`, so
 * blanking one is treated as "not edited" rather than "erase this". The API would accept the
 * clear — `cleanDemographicsForUpdate` filters only `undefined` — but these are identity
 * fields, and making a mis-click erase a patient's date of birth (which referral matching
 * depends on) is a product decision, not a consistency fix. Raised in the brief's open
 * questions; if Ops wants clearable demographics, this is the one place to change.
 *
 * **Height, weight and BMI are the exception: they can be cleared.** They are measurements, not
 * identity: the extractor can misread them, and an operator facing a mis-extracted 310 lb had
 * no way to remove it. Blanking one that `storedDemographics` holds sends `null`, which
 * leads-api accepts (`@IsOptional() @IsNumber()`) and stores over the old value; blanking one
 * that was never stored sends nothing.
 *
 * `loadedInsurance` is the rows the form started from (the output of `buildEditableInsurance`),
 * whether they came from the referral's stored coverage or were pre-filled from the document's
 * extraction. Untouched rows are still sent, the same rule the card applies to every other
 * pre-filled field, so a fax giving a payer *and* a policy id is stored by any save. The one
 * exception: when the untouched rows would fail `primaryCoverageProblem` (a stored primary
 * carrying only detail, or a fax that names a payer but no policy id), `insurance` is left out
 * and the API keeps what it holds. Sending those back made the API refuse every save of the
 * card, a clinic change included, with no field the operator had touched to explain it. Once a
 * coverage field is edited the rows are sent and checked. `null` means nothing was loaded, so
 * `insurance` is always sent.
 */
export const buildReferralUpdateRequest = (
  editData: ReferralEditState,
  loadedInsurance: ReferralInsurance[] | null = null,
  storedDemographics: ReferralDemographics | null = null,
): UpdateReferralRequest => {
  const telephone = PHONE_FIELDS.map(({ key, type }) => ({
    type,
    number: String(editData[key] ?? '').trim(),
  })).filter((phone) => phone.number.length > 0);

  const measurement = (key: 'bmi' | 'heightFeet' | 'heightInch' | 'weightLb') =>
    parseMeasurement(editData[key], storedDemographics?.[key]);

  const demographics: ReferralDemographics = {
    firstName: editData.firstName || undefined,
    lastName: editData.lastName || undefined,
    dateOfBirth: editData.dateOfBirth || undefined,
    gender: editData.gender || undefined,
    street: editData.street || undefined,
    city: editData.city || undefined,
    state: editData.state || undefined,
    zipCode: editData.zipCode || undefined,
    email: editData.email || undefined,
    telephone: telephone.length > 0 ? telephone : undefined,
    bmi: measurement('bmi'),
    heightFeet: measurement('heightFeet'),
    heightInch: measurement('heightInch'),
    weightLb: measurement('weightLb'),
  };

  // A row the operator added and left empty is not a coverage — but "empty" means every field,
  // not just the three editable ones. A stored coverage can carry only detail the form does
  // not edit (a subscriber, a coverage window), and dropping it here would erase that detail
  // on the next save, which is the loss the load side was fixed for.
  const rows = editData.insurance?.filter(hasAnyCoverageValue);
  const untouchedAndRefused =
    editableCoverageUnchanged(loadedInsurance, editData.insurance) &&
    primaryCoverageProblem({ insurance: rows }) !== null;
  const insurance = untouchedAndRefused ? undefined : rows;

  const updateRequest: UpdateReferralRequest = {
    clinicId: editData.businessClinicId || undefined,
    demographics,
    insurance: insurance?.length ? insurance : undefined,
    isCommunityReferral: editData.isCommunityReferral,
    systemSource: editData.systemSource as LeadSystemSource,
    referringProvider: editData.referringProvider?.trim() ?? undefined,
    // Only sent for a community referral: the input is hidden otherwise, so sending stale
    // state would write a name the operator cannot see. `''` when it is a community referral
    // and the box was cleared.
    communityReferralClinicName: editData.isCommunityReferral
      ? (editData.communityReferralClinicName?.trim() ?? '')
      : undefined,
  };

  stripUndefined(updateRequest as unknown as Record<string, unknown>);
  stripUndefined(demographics as unknown as Record<string, unknown>);

  return updateRequest;
};

/** Drops "not edited" keys, in place, leaving `''` alone so a clear still reaches the API. */
const stripUndefined = (target: Record<string, unknown>): void => {
  Object.keys(target).forEach((key) => {
    if (target[key] === undefined) {
      delete target[key];
    }
  });
};

/**
 * True when the rows on the form match the rows it loaded, row for row — an added or removed
 * row counts as a change. Every key of either row is compared rather than a list of the inputs
 * the card shows, so a field that becomes editable later cannot have its edit dropped as
 * "unchanged".
 */
export const editableCoverageUnchanged = (
  loaded: ReferralInsurance[] | null,
  current: ReferralInsurance[] | undefined,
): boolean => {
  if (!loaded || !current || loaded.length !== current.length) {
    return false;
  }

  return loaded.every((row, index) => {
    const before = row as unknown as Record<string, unknown>;
    const after = current[index] as unknown as Record<string, unknown>;
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    return keys.every((key) => comparable(before[key]) === comparable(after[key]));
  });
};

/** A coverage value as compared for "unchanged": absent, empty and whitespace are the same. */
const comparable = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }
  return typeof value === 'string' ? value.trim() : JSON.stringify(value);
};

/** True when a coverage row carries anything besides its `type`. */
const hasAnyCoverageValue = (coverage: ReferralInsurance): boolean =>
  Object.entries(coverage).some(
    ([field, value]) => field !== 'type' && typeof value === 'string' && value.trim().length > 0,
  );

/**
 * Why the API would refuse this body's primary coverage, or `null` when it would not.
 *
 * leads-api requires a carrier and a policy number on the primary coverage whenever insurance
 * is sent. Untouched rows it would refuse are left out of the body (see `loadedInsurance`
 * above), so this only fires on an edit the API would refuse, and names the field rather than
 * leaving the operator with the API's generic failure.
 */
export const primaryCoverageProblem = (
  request: Pick<UpdateReferralRequest, 'insurance'>,
): string | null => {
  const primary = request.insurance?.find((coverage) => coverage.type === 'primary');
  if (!primary) {
    return null;
  }

  const missing = [
    !primary.carrier?.trim() && 'Carrier',
    !primary.policyNumber?.trim() && 'Policy Number',
  ].filter(Boolean);

  return missing.length
    ? `The primary insurance needs ${missing.join(' and ')} before this referral can be saved.`
    : null;
};

/**
 * A measurement input as the API takes it: a number; `null` to clear a stored value; or absent
 * when blank with nothing stored, or not a number. Range checks happen before the save
 * (`measurementProblem`), which refuses to send an out-of-range value at all.
 */
const parseMeasurement = (
  value: string | undefined,
  stored: number | null | undefined,
): number | null | undefined => {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') {
    return stored != null ? null : undefined;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};
